import { useEffect, useState, type ReactNode } from 'react';
import { ThemeContext } from './ThemeContext';
import {
  generateCssVariables,
  reefGuideDarkTheme,
  reefGuideLightTheme,
} from '../design-system/tokens';

// ── Helpers ───────────────────────────────────────────────────────────────────

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'reefguide-theme';

function getSystemPreference(): ThemeMode {
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'dark'; // default to dark
}

function getSavedMode(): ThemeMode | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') {
      return saved;
    }
  } catch {
    // localStorage not available
  }
  return null;
}

function saveMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // localStorage not available
  }
}

function injectCssVariables(mode: ThemeMode): void {
  const dsTheme = mode === 'dark' ? reefGuideDarkTheme : reefGuideLightTheme;
  const variables = generateCssVariables(dsTheme);
  const root = document.documentElement;
  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}

// ── Provider ──────────────────────────────────────────────────────────────────

export interface ThemeProviderProps {
  children: ReactNode;
  /** Override the initial theme mode (useful for testing) */
  initialMode?: ThemeMode;
  /** Alias for initialMode — accepted for test-compatibility */
  initialTheme?: ThemeMode;
  /** Optional explicit token map exposed via context (defaults to the official tokens of the active theme) */
  tokens?: Readonly<Record<string, string>>;
  /** Optional theme name set as data-theme attribute on the wrapper div */
  themeName?: string;
}

export function ThemeProvider({
  children,
  initialMode,
  initialTheme,
  tokens: tokensProp,
  themeName = 'default',
}: ThemeProviderProps): JSX.Element {
  const [theme, setThemeState] = useState<ThemeMode>(
    // Priority: explicit initialMode override → localStorage → initialTheme default → OS preference
    () => initialMode ?? getSavedMode() ?? initialTheme ?? getSystemPreference(),
  );

  // Resolve tokens for context consumers that read them (e.g. tests, charts)

  // Inject design system CSS variables AND set data-theme + class list
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark', 'light');
    root.classList.add(theme);
    root.setAttribute('data-theme', theme);
    injectCssVariables(theme);
  }, [theme]);

  const setTheme = (next: ThemeMode): void => {
    saveMode(next);
    setThemeState(next);
  };

  const toggleTheme = (): void => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const value = {
    themeName,
    tokens: tokensProp ?? generateCssVariables(theme === 'dark' ? reefGuideDarkTheme : reefGuideLightTheme),
    theme,
    toggleTheme,
    toggle: toggleTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// ── Re-export token keys for consumers ────────────────────────────────────────

// The official design-system tokens (src/design-system/tokens) are the only
// token vocabulary. Keys are exported without the leading `--`.
const officialTokens: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(generateCssVariables(reefGuideDarkTheme)).map(([k, v]) => [k.slice(2), v]),
);

/** All official token keys (without the -- prefix) */
export const semanticTokenKeys: ReadonlyArray<string> = Object.keys(officialTokens);

/** Official token map (dark theme values), keyed without the -- prefix */
export { officialTokens as semanticTokens };
